import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Office } from "../../../../../../../models/office";
import { Filters, Searches, Sort, Sorts } from "../../../../../../../models/shipeezi.table";
import OfficeService from "../../../../../../../services/office.service";
import { OfficeKeys } from "../../../../../../../util/queries";

export const useActions = () => {
  const { currentUser } = useAppState();
  const queryClient = useQueryClient();

  const [offices, setOffices] = useState<Office[]>([]);
  const [office, setOffice] = useState<Office>();
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery({
    queryKey: OfficeKeys.getAll([page, pageSize, filters, searches, sorting]),
    queryFn: () =>
      OfficeService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "ports" }, { association: "country" }]),
        sortBy: sorting ? JSON.stringify(sorting) : JSON.stringify([["createdAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    enabled: !!currentUser?.id,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response.data) setOffices(response.data);
    },
  });

  const { refetch: getOfficesTotal } = useQuery({
    queryKey: OfficeKeys.count([filters, searches]),
    queryFn: () =>
      OfficeService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    enabled: !!currentUser?.id,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response.data) setTotal(response.data);
    },
  });

  const { mutate: createOffice, isLoading: isCreating } = useMutation({
    mutationKey: OfficeKeys.create(),
    mutationFn: (office: Office) => OfficeService.create(office),
    onSuccess: () => {
      setHasErrors(false);
      refetch();
      getOfficesTotal();

      queryClient.invalidateQueries({ queryKey: [OfficeKeys.all], refetchType: "none" });
    },
    onError: (err: any) => {
      NotificationToast.error(err.response.data);
      setHasErrors(true);
    },
  });

  const { mutate: updateOffice, isLoading: isUpdating } = useMutation({
    mutationKey: OfficeKeys.update(),
    mutationFn: ({ id, office }: { id: number; office: Office }) =>
      OfficeService.update(id, office),
    onSuccess: () => {
      setHasErrors(false);
      refetch();
      getOfficesTotal();

      queryClient.invalidateQueries({ queryKey: [OfficeKeys.all], refetchType: "none" });
    },
    onError: (err: any) => {
      NotificationToast.error(err.response.data);
      setHasErrors(true);
    },
  });

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    variables: {
      offices,
      office,
      hasErrors,
      page,
      pageSize,
      total,
      isFetching,
      isCreating,
      isUpdating,
    },
    setPageSize,
    setOffice,
    createOffice,
    updateOffice,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
  };
};
